/**
 * **Layer B** of the morph: a static, inert preview of the question panel at
 * **target width** (400 px).
 *
 * Already during the flight it shows exactly the line wrapping the real panel
 * will have afterwards — so at the end there is no jump, just a 120 ms
 * crossfade between two nearly identical images. Borders and input box come
 * from `assistantPanelChrome` — the same definition `AppShell` renders; the
 * panel has had no header since 22.09.2026, so the preview has none either.
 *
 * Capped at six messages: no more are visible in a 400 px wide column anyway,
 * and sending a 200-message transcript through markdown a second time would
 * cost exactly the first frames of the flight.
 *
 * No help text **as long as there is something to adopt**: after a handoff the
 * real panel no longer shows an opening bubble either (product owner decision
 * of 22.09.2026) — it starts directly with the adopted messages. If the
 * history is empty (handoff without a conversation), the opening bubble is in
 * both images instead.
 *
 * Scroll position: the preview sits at the **end**, which is also where the
 * real panel's `MessageScroller` lands on mount. If the reader was far up in
 * the main chat, that is a deliberate difference: there is no well-defined
 * mapping of a scroll position between a 1400 px and a 400 px wide column, and
 * the real panel sits at the end.
 */

import { useLayoutEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { ChatConversation } from "@/components/ChatConversation";
import {
  ASSISTANT_PANEL_ACTION_ROW_CLASS,
  ASSISTANT_PANEL_FORM_CLASS,
  ASSISTANT_PANEL_INPUT_BOX_CLASS,
  ASSISTANT_PANEL_TRANSCRIPT_CLASS,
} from "@/components/overlay-bot/assistantPanelChrome";
import { adoptedMessages } from "@/components/overlay-bot/OverlayBotChat";
import styles from "@/components/overlay-bot/OverlayBotChat.module.sass";

/** This many messages are shown in the preview — see the header comment. */
export const PREVIEW_MESSAGE_LIMIT = 6;

export default function AssistantPanelPreview({ snapshot }: { snapshot: unknown }) {
  const { t } = useTranslation();
  const transcriptRef = useRef<HTMLDivElement>(null);

  const conversation = useMemo(() => {
    const adopted = adoptedMessages(snapshot).slice(-PREVIEW_MESSAGE_LIMIT);
    return {
      _id: "morph-preview",
      // Nothing to adopt (handoff without a conversation): then the real panel
      // does not show an empty area either but its opening bubble — otherwise
      // the crossfade would fade from "empty" to "help bubble". The preview
      // does not know the page-specific help text; the general one is the right
      // approximation.
      messages:
        adopted.length > 0
          ? adopted
          : [
              {
                id: "morph-preview-help",
                type: "botMessage" as const,
                content: t("overlayBot.defaultHelp"),
                resources: [] as never[],
              },
            ],
    };
  }, [snapshot, t]);

  // To the end — that is also where the real panel lands on mount
  // (`justify-end`, `min-h-full` in the `MessageScroller`).
  useLayoutEffect(() => {
    const scroller = transcriptRef.current?.querySelector<HTMLElement>(".messages");
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, [conversation]);

  return (
    <div className="flex h-full w-full flex-col bg-background">
      <div
        ref={transcriptRef}
        data-testid="morph-preview-transcript"
        className={`${ASSISTANT_PANEL_TRANSCRIPT_CLASS} ${styles.transcript}`}
      >
        <ChatConversation
          conversation={conversation}
          pendingRequest={false}
          submitInput={() => undefined}
          onFeedback={() => undefined}
          compactActions
        />
      </div>
      {/* Dead copy of the input box: same classes, no `textarea`, no button —
          the shell must not catch focus. */}
      <div className={ASSISTANT_PANEL_FORM_CLASS}>
        <div className={ASSISTANT_PANEL_INPUT_BOX_CLASS}>
          <div className="h-12 px-1 pt-0.5 text-[15px] leading-6 text-[var(--oa-fg-4)]">
            {t("overlayBot.placeholder")}
          </div>
          <div className={ASSISTANT_PANEL_ACTION_ROW_CLASS}>
            <div className="ml-auto h-9 w-9 shrink-0 rounded-full bg-primary" />
          </div>
        </div>
      </div>
    </div>
  );
}
